import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { IUser } from 'src/app/domain/registration/models/IUser';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { S3_URL_BASE } from 'src/environments/environment';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { UserService } from '../crud.service';
import { UserPermissionService } from '../../user-permissions/crud-service';
import { FrontFirstTreeGroupDto } from 'src/app/shared/components/front-first-permission-tree/front-first-permission-tree.types';
import { PermissionGroupService } from '../../permission-groups/crud-service';
import {
  PermissionEntry,
  PermissionGroupCatalogEntry,
} from 'src/app/shared/components/permission-selector/permission-selector.component';

@Component({
  selector: 'app-user-profile-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
})
export class UserProfileCESComponent implements OnInit, OnDestroy {
  /** Dados carregados do servidor */
  data: IUser | null = null;

  // ---- Secao 2: Dados pessoais ----
  profileForm: FormGroup;
  editingProfile = false;
  savingProfile = false;
  /** Preview base64 do novo avatar antes de salvar */
  avatarPreview: string | null = null;

  // ---- Secao 3: Seguranca ----
  passwordForm: FormGroup;
  savingPassword = false;

  // ---- Secao 4: Minhas permissoes ----
  /** Cache RAW da API (view-only, disabled=true — sem save nesta tela). */
  userPermissionsRaw: any[] = [];
  /** Lista plana pro app-permission-selector. */
  userPermissions: PermissionEntry[] = [];
  /** IDs concedidos (contrato imutável — só leitura aqui, disabled=true). */
  userPermissionSelectedIds: string[] = [];
  permissionGroups: PermissionGroupCatalogEntry[] = [];
  permissionsLoading = false;
  /** Árvore "Por tela" (own-only = as próprias permissões). Vazia/erro → só Simples/Avançado. */
  frontFirstGroups: FrontFirstTreeGroupDto[] = [];
  frontFirstLoading = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private service: UserService,
    private userPermissionService: UserPermissionService,
    private permissionGroupService: PermissionGroupService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.loadUser();
  }

  ngOnDestroy(): void {}

  private initForms(): void {
    this.profileForm = this.fb.group({
      firstName: [null, [Validators.required, Validators.minLength(2)]],
      lastName:  [null, [Validators.required, Validators.minLength(2)]],
      email:     [null, [Validators.required, Validators.email]],
      phone:     [null, []],
      document:  [null, []],
    });

    this.passwordForm = this.fb.group({
      password:        [null, [Validators.required, Validators.minLength(4)]],
      newPassword:     [null, [Validators.required, Validators.minLength(4)]],
      confirmPassword: [null, [Validators.required, Validators.minLength(4)]],
    });
  }

  private loadUser(): void {
    // Usa o ID do JWT (authService) e nao da URL para evitar IDOR —
    // um usuario nao pode ver/editar o perfil de outro via manipulacao da rota.
    const id = this.authService.getUserId();
    if (!id) return;

    // Leitura por `GET /users/me` (self no backend, sem id na rota): nao da para ler o perfil de outro usuario.
    this.service.getMe().pipe(take(1)).subscribe({
      next: (data: any) => {
        this.data = data;
        this.profileForm.patchValue({
          firstName: data.firstName,
          lastName:  data.lastName,
          email:     data.email,
          phone:     data.cellPhone || data.phone || null,
          document:  data.document || null,
        });
        this.loadPermissions(id);
        this.loadPermissionGroups();
        this.loadFrontFirstTree();
      },
      error: () => this.toastService.showError('Erro ao carregar perfil.'),
    });
  }

  /**
   * Carrega as PRÓPRIAS permissões granulares via `GET /users/my-permissions`
   * (self, via req.user no backend) -- NUNCA via `UserPermissionService.listAll`
   * (endpoint ADMIN, `GET /user-permissions?user=<id>`, gateado por
   * `UserPermissionsController_findAll`): um usuario comum sem permissao de
   * gerenciar OUTROS usuarios tomaria 403 tentando ver o PROPRIO perfil ali,
   * mesmo passando o proprio id. O endpoint self dispensa esse gate (ver
   * AuthorizationInterceptor "Public Routes").
   */
  private loadPermissions(_userId: string): void {
    this.permissionsLoading = true;
    this.service
      .getMyPermissions()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const data = Array.isArray(response?.[0]) ? response[0] : (Array.isArray(response) ? response : []);
          this.userPermissionsRaw = data;
          this.userPermissions = data.map((item: any) => ({
            id: String(item.systemActionParamId ?? item.id),
            name: item.name,
          }));
          this.userPermissionSelectedIds = data
            .filter((item: any) => !!item.allow)
            .map((item: any) => String(item.systemActionParamId ?? item.id));
        },
        error: () => { this.permissionsLoading = false; },
        complete: () => { this.permissionsLoading = false; },
      });
  }

  /** Carrega a árvore "Por tela" (somente leitura); qualquer falha só esconde o modo. */
  private loadFrontFirstTree(): void {
    this.frontFirstLoading = true;
    this.permissionGroupService
      .getFrontFirstTree()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.frontFirstGroups = Array.isArray(res?.groups) ? res.groups : [];
          this.frontFirstLoading = false;
        },
        error: () => {
          this.frontFirstGroups = [];
          this.frontFirstLoading = false;
        },
      });
  }

  private loadPermissionGroups(): void {
    this.permissionGroupService
      .listAll({ getRelation: 1, limit: 5000, page: 0, filter: '' })
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const rows = Array.isArray(response?.[0]) ? response[0] : (Array.isArray(response) ? response : []);
          this.permissionGroups = rows
            .map((g: any) => ({
              id: String(g?.id || ''),
              name: String(g?.name || ''),
              permissionIds: Array.isArray(g?.permissionIds)
                ? g.permissionIds.map((id: any) => String(id))
                : Array.isArray(g?.permissions)
                  ? g.permissions.map((p: any) => String(p?.id || '')).filter(Boolean)
                  : [],
            }))
            .filter((g: PermissionGroupCatalogEntry) => !!g.id && !!g.name);
        },
      });
  }

  // ---- Identidade ----

  public getAvatarUrl(): string {
    if (this.avatarPreview) return this.avatarPreview;
    if (this.data?.avatarUrl) return `${S3_URL_BASE}${this.data.avatarUrl}`;
    return 'assets/img/Imagem arquivo errado.png';
  }

  public getUserGroupNames(): string[] {
    const groups = (this.data as any)?.userGroups;
    if (!Array.isArray(groups)) return [];
    return groups
      .map((g: any) =>
        typeof g === 'string' ? g : (g?.description || g?.name || '')
      )
      .filter(Boolean);
  }

  // ---- Secao 2: Dados pessoais ----

  public startEditProfile(): void {
    this.editingProfile = true;
  }

  public cancelEditProfile(): void {
    this.editingProfile = false;
    this.avatarPreview = null;
    if (this.data) {
      this.profileForm.patchValue({
        firstName: this.data.firstName,
        lastName:  this.data.lastName,
        email:     this.data.email,
        phone:     (this.data as any).cellPhone || (this.data as any).phone || null,
        document:  (this.data as any).document || null,
      });
    }
  }

  public onAvatarFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;
    const reader = new FileReader();
    reader.onload = () => { this.avatarPreview = reader.result as string; };
    reader.readAsDataURL(input.files[0]);
  }

  public async saveProfile(): Promise<void> {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.toastService.showWarn('Preencha os campos corretamente.');
      return;
    }
    if (!this.data?.id) return;

    this.savingProfile = true;
    const v = this.profileForm.value;
    const formData = new FormData();

    if (this.avatarPreview && this.avatarPreview.includes(';base64')) {
      const ext = this.avatarPreview.toLowerCase().includes('png') ? 'png' : 'jpeg';
      const blob = await GlobalUtils.blobImageFromBase64String(this.avatarPreview);
      formData.append('image', new File([blob], `profile_${this.data.id}.${ext}`));
    }

    // Apenas campos pessoais — sem grupo, sem permissao (previne auto-elevacao)
    formData.append('data', JSON.stringify({
      firstName: v.firstName,
      lastName:  v.lastName,
      email:     v.email,
      cellPhone: v.phone,
      document:  v.document,
    }));

    this.service.updateWithFormData(this.data.id, formData)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showSuccess('Dados atualizados com sucesso!');
          this.editingProfile = false;
          this.avatarPreview = null;
          this.loadUser();
        },
        error: (err: any) => {
          const raw = err?.error?.message || err?.message;
          this.toastService.showError(Array.isArray(raw) ? raw.join('; ') : (raw || 'Erro ao atualizar os dados.'));
          this.savingProfile = false;
        },
        complete: () => { this.savingProfile = false; },
      });
  }

  // ---- Secao 3: Seguranca ----

  public savePassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    const { password, newPassword, confirmPassword } = this.passwordForm.value;
    if (newPassword !== confirmPassword) {
      this.toastService.showWarn(MessagesEnum.PASSWORD_NOT_MATCH);
      return;
    }
    if (!this.data?.id) return;

    this.savingPassword = true;
    this.service
      .updateMyPassword({ password, newPassword, confirmPassword })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showSuccess(MessagesEnum.SUCCESS_PASSWORD_CHANGE);
          this.passwordForm.reset();
        },
        error: (err: any) => {
          const raw = err?.error?.message || err?.message;
          this.toastService.showError(Array.isArray(raw) ? raw.join('; ') : (raw || 'Erro ao alterar a senha.'));
          this.savingPassword = false;
        },
        complete: () => { this.savingPassword = false; },
      });
  }

  public back(): void {
    this.router.navigate(['dashboard']);
  }
}
